import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Calculator, Download } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PropertyInfoSection from './PropertyInfoSection';
import OwnershipSection from './OwnershipSection';
import RegionAndRatesSection from './RegionAndRatesSection';
import PrivilegesSection from './PrivilegesSection';
import AdditionalParametersSection from './AdditionalParametersSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета налога на имущество
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  generateTaxBreakdown,
  propertyTypes,
  regionTaxRates,
  privilegeTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Сложные поля (вложенные объекты)
    if (name.includes('.')) {
      const [parentKey, childKey] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parentKey]: {
          ...prev[parentKey],
          [childKey]: fieldValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: fieldValue
      }));
    }

    // Дополнительная логика при изменении определенных полей
    if (name === 'propertyType') {
      // Сброс некоторых полей при изменении типа имущества
      setFormData(prev => ({
        ...prev,
        [name]: fieldValue,
        // Сбросим параметры специфичные для типа
        useDeduction: ['apartment', 'room', 'house', 'cottage'].includes(fieldValue)
      }));
    }

    if (name === 'hasPrivileges' && !fieldValue) {
      // Сброс привилегий, если они отключены
      setFormData(prev => ({
        ...prev,
        hasPrivileges: false,
        privilegeType: 'none',
        personalData: {
          ...prev.personalData,
          isPensioner: false,
          isVeteran: false,
          isDisabled: false,
          isHero: false,
          isMilitaryPerson: false
        }
      }));
    }

    if (name === 'privilegeType') {
      // Автоматическое обновление личных данных при выборе привилегии
      const personalData = { ...formData.personalData };
      
      // Сброс всех флагов
      Object.keys(personalData).forEach(key => {
        personalData[key] = false;
      });
      
      // Установка соответствующего флага
      switch(fieldValue) {
        case 'pensioner':
          personalData.isPensioner = true;
          break;
        case 'veteran':
          personalData.isVeteran = true;
          break;
        case 'disabled':
          personalData.isDisabled = true;
          break;
        case 'hero':
          personalData.isHero = true;
          break;
        case 'military':
          personalData.isMilitaryPerson = true;
          break;
        default:
          // Для других типов льгот не устанавливаем специальных флагов
      }
      
      setFormData(prev => ({
        ...prev,
        privilegeType: fieldValue,
        personalData
      }));
    }

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [formData, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      propertyType, cadastralValue, area, ownershipShare, ownershipDuration,
      region, hasPrivileges, privilegeType, propertyData, taxYear, useDeduction,
      includeDepreciation, depreciationPercent
    } = formData;

    // Парсинг числовых значений
    const numCadastralValue = parseFloatOrDefault(cadastralValue, -1);
    const numArea = parseFloatOrDefault(area, -1);
    const numOwnershipShare = parseFloatOrDefault(ownershipShare, -1);
    const numOwnershipDuration = parseIntOrDefault(ownershipDuration, -1);
    const numDepreciationPercent = parseFloatOrDefault(depreciationPercent, -1);

    // Валидация базовых полей
    if (numCadastralValue <= 0) newErrors.cadastralValue = 'Кадастровая стоимость должна быть положительной.';
    if (!newErrors.cadastralValue && numCadastralValue > 1000000000) newErrors.cadastralValue = 'Максимальная стоимость 1 млрд ₽.';
    
    if (numArea <= 0) newErrors.area = 'Площадь должна быть положительной.';
    if (!newErrors.area && numArea > 10000) newErrors.area = 'Максимальная площадь 10 000 кв.м.';
    
    if (numOwnershipShare <= 0 || numOwnershipShare > 1) newErrors.ownershipShare = 'Доля должна быть от 0 до 1 (0.5 = 50%).';
    
    if (numOwnershipDuration < 0 || numOwnershipDuration > 12) newErrors.ownershipDuration = 'Период владения от 0 до 12 месяцев.';
    
    if (includeDepreciation && (numDepreciationPercent < 0 || numDepreciationPercent > 100)) {
      newErrors.depreciationPercent = 'Процент износа должен быть от 0 до 100%.';
    }

    // Проверка года постройки
    const constructionYear = parseIntOrDefault(propertyData.constructionYear, 0);
    const currentYear = new Date().getFullYear();
    if (constructionYear <= 1800 || constructionYear > currentYear) {
      newErrors['propertyData.constructionYear'] = `Год постройки должен быть между 1800 и ${currentYear}.`;
    }

    // Проверка года налогового периода
    if (taxYear < 2015 || taxYear > currentYear) {
      newErrors.taxYear = `Налоговый период должен быть между 2015 и ${currentYear}.`;
    }

    // Проверка наличия региона
    if (!region) {
      newErrors.region = 'Необходимо выбрать регион.';
    }

    // Проверка типа льготы если выбраны льготы
    if (hasPrivileges && privilegeType === 'none') {
      newErrors.privilegeType = 'Выберите тип льготы.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет налога на имущество
  const calculatePropertyTax = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const cadastralValue = parseFloatOrDefault(formData.cadastralValue);
        const area = parseFloatOrDefault(formData.area);
        const ownershipShare = parseFloatOrDefault(formData.ownershipShare);
        const ownershipDuration = parseIntOrDefault(formData.ownershipDuration);
        const selectedRegion = regionTaxRates.find(r => r.id === formData.region) || regionTaxRates[0];
        const propertyType = formData.propertyType;
        const taxYear = formData.taxYear;
        const useDeduction = formData.useDeduction;
        
        // --- Определение налоговой ставки ---
        let taxRate;
        
        // Для квартир ставка может зависеть от стоимости (в Москве и СПб)
        if (propertyType === 'apartment') {
          if (cadastralValue <= 10000000) {
            taxRate = selectedRegion.rates.apartment;
          } else if (cadastralValue <= 20000000) {
            taxRate = selectedRegion.rates.apartmentHigh;
          } else if (cadastralValue <= 50000000) {
            taxRate = selectedRegion.rates.apartmentPremium;
          } else if (cadastralValue <= 300000000) {
            taxRate = selectedRegion.rates.apartmentLuxury;
          } else {
            taxRate = selectedRegion.rates.apartmentUltraLuxury;
          }
        } else if (propertyType === 'land') {
          taxRate = selectedRegion.rates.land;
        } else if (propertyType === 'office') {
          taxRate = selectedRegion.rates.office;
        } else if (propertyType === 'house' || propertyType === 'cottage') {
          taxRate = selectedRegion.rates.house;
        } else if (propertyType === 'garage') {
          taxRate = selectedRegion.rates.garage;
        } else {
          taxRate = selectedRegion.rates.other;
        }

        // --- Расчет налогового вычета ---
        let deductionArea = 0;
        let deduction = 0;
        
        if (useDeduction) {
          // Налоговый вычет применяется только к определенным типам имущества
          if (propertyType === 'apartment') {
            deductionArea = selectedRegion.deductions.apartment || 20;
          } else if (propertyType === 'room') {
            deductionArea = selectedRegion.deductions.room || 10;
          } else if (propertyType === 'house') {
            deductionArea = selectedRegion.deductions.house || 50;
          } else if (propertyType === 'cottage') {
            deductionArea = selectedRegion.deductions.cottage || 50;
          }
          
          // Вычет не может быть больше фактической площади
          deductionArea = Math.min(deductionArea, area);
          
          // Расчет суммы вычета: стоимость 1 кв.м. * площадь вычета
          const costPerSquareMeter = cadastralValue / area;
          deduction = costPerSquareMeter * deductionArea * taxRate / 100;
        }
        
        // --- Расчет понижающего коэффициента для новостроек ---
        let reducingCoefficientValue = 1;
        
        if (formData.useReducingCoefficient) {
          const constructionYear = parseIntOrDefault(formData.propertyData.constructionYear);
          const currentYear = taxYear;
          const yearsSinceConstruction = currentYear - constructionYear;
          
          // Понижающий коэффициент применяется для объектов не старше 5 лет с момента постройки
          if (yearsSinceConstruction <= 5 && yearsSinceConstruction >= 0) {
            // Для каждого года свой коэффициент
            switch(yearsSinceConstruction) {
              case 0:
              case 1:
                reducingCoefficientValue = 0.2; // 20% от налога в первый год
                break;
              case 2:
                reducingCoefficientValue = 0.4; // 40% от налога во второй год
                break;
              case 3:
                reducingCoefficientValue = 0.6; // 60% от налога в третий год
                break;
              case 4:
                reducingCoefficientValue = 0.8; // 80% от налога в четвертый год
                break;
              default:
                reducingCoefficientValue = 1; // 100% налога с пятого года
            }
          }
        }
        
        // --- Расчет базового налога ---
        const baseTax = (cadastralValue * taxRate) / 100;
        
        // --- Расчет налога с учетом вычета ---
        const taxAfterDeduction = Math.max(0, baseTax - deduction);
        
        // --- Расчет налога с учетом понижающего коэффициента ---
        const taxAfterReducingCoefficient = taxAfterDeduction * reducingCoefficientValue;
        
        // --- Учет доли в собственности ---
        const taxAfterOwnershipShare = taxAfterReducingCoefficient * ownershipShare;
        
        // --- Учет периода владения в налоговом периоде ---
        const ownershipPeriodFactor = ownershipDuration / 12;
        const taxAfterOwnershipPeriod = taxAfterOwnershipShare * ownershipPeriodFactor;
        
        // --- Учет налоговых льгот ---
        let privilegeDiscount = 0;
        let privilegeDescription = '';
        
        if (formData.hasPrivileges) {
          // Полное освобождение от налога для некоторых категорий граждан
          // в отношении одного объекта каждого вида недвижимости
          const fullExemptionCategories = ['pensioner', 'disabled', 'veteran', 'hero', 'chernobyl'];
          
          if (fullExemptionCategories.includes(formData.privilegeType)) {
            privilegeDiscount = taxAfterOwnershipPeriod;
            
            // Получаем описание льготы
            const privilege = privilegeTypes.find(p => p.id === formData.privilegeType);
            privilegeDescription = privilege ? privilege.name : 'Льготная категория';
          } else if (formData.privilegeType === 'military') {
            // Военнослужащие имеют льготу в размере до 50% от суммы налога
            privilegeDiscount = taxAfterOwnershipPeriod * 0.5;
            privilegeDescription = 'Военнослужащий (скидка 50%)';
          } else if (formData.privilegeType === 'other') {
            // Прочие льготы могут быть разными, берем среднее значение 30%
            privilegeDiscount = taxAfterOwnershipPeriod * 0.3;
            privilegeDescription = 'Иные льготы (скидка 30%)';
          }
        }
        
        // --- Итоговый налог ---
        const finalTax = Math.max(0, taxAfterOwnershipPeriod - privilegeDiscount);

        // --- Формирование результатов ---
        const calculationResults = {
          propertyType,
          regionName: selectedRegion.name,
          taxYear,
          cadastralValue,
          area,
          taxRate,
          
          baseTax,
          
          deductionArea,
          deduction,
          
          reducingCoefficientValue,
          
          ownershipShareFactor: ownershipShare,
          taxBeforeShare: taxAfterReducingCoefficient,
          
          ownershipPeriodFactor,
          taxBeforePeriod: taxAfterOwnershipShare,
          
          privilegeDiscount,
          privilegeDescription,
          
          finalTax,
          
          // Дополнительная информация
          calculationDate: new Date().toISOString(),
          debugInfo: {
            propertyTypeName: propertyTypes.find(t => t.id === propertyType)?.name,
            regionRates: { ...selectedRegion.rates },
            deductions: { ...selectedRegion.deductions }
          }
        };

        setResults(calculationResults);
        generateTaxBreakdown(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, generateTaxBreakdown, regionTaxRates, propertyTypes, privilegeTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      propertyType: 'apartment',
      cadastralValue: '5000000',
      area: '50',
      ownershipShare: '1',
      ownershipDuration: '12',
      region: 'moscow',
      hasPrivileges: false,
      privilegeType: 'none',
      personalData: {
        isPensioner: false,
        isVeteran: false,
        isDisabled: false,
        isHero: false,
        isMilitaryPerson: false,
      },
      propertyData: {
        constructionYear: '2010',
        registrationDate: new Date().toISOString().split('T')[0],
        isHistorical: false,
        isCulturalHeritage: false,
      },
      taxYear: new Date().getFullYear() - 1,
      useDeduction: true,
      deductionType: 'standard',
      useReducingCoefficient: true,
      includeDepreciation: false,
      depreciationPercent: '0',
    });
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт расчета в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Форматирование значений для CSV
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');
    
    // Подготовка строк CSV
    const infoRows = [
      ['Расчет налога на имущество физических лиц'],
      [''],
      ['Параметры расчета:'],
      ['Тип имущества:', results.debugInfo.propertyTypeName],
      ['Регион:', results.regionName],
      ['Налоговый период:', results.taxYear],
      ['Кадастровая стоимость:', `${formatCsvNumber(results.cadastralValue)} ₽`],
      ['Площадь объекта:', `${formatCsvNumber(results.area)} кв.м.`],
      ['Налоговая ставка:', `${results.taxRate}%`],
      [''],
      ['Результаты расчета:'],
      ['Базовый налог:', `${formatCsvNumber(results.baseTax)} ₽`],
    ];
    
    // Добавляем информацию о вычетах
    if (results.deduction > 0) {
      infoRows.push(['Налоговый вычет:', `${formatCsvNumber(results.deduction)} ₽`]);
      infoRows.push(['Площадь вычета:', `${results.deductionArea} кв.м.`]);
    }
    
    // Добавляем информацию о понижающем коэффициенте
    if (results.reducingCoefficientValue < 1) {
      infoRows.push(['Понижающий коэффициент:', results.reducingCoefficientValue]);
    }
    
    // Добавляем информацию о доле владения
    if (results.ownershipShareFactor < 1) {
      infoRows.push(['Доля в собственности:', `${results.ownershipShareFactor * 100}%`]);
    }
    
    // Добавляем информацию о периоде владения
    if (results.ownershipPeriodFactor < 1) {
      infoRows.push(['Период владения:', `${results.ownershipPeriodFactor * 12} месяцев`]);
    }
    
    // Добавляем информацию о льготах
    if (results.privilegeDiscount > 0) {
      infoRows.push(['Налоговая льгота:', `${formatCsvNumber(results.privilegeDiscount)} ₽`]);
      infoRows.push(['Категория льготы:', results.privilegeDescription]);
    }
    
    // Итоговый налог
    infoRows.push(['']);
    infoRows.push(['ИТОГОВАЯ СУММА НАЛОГА:', `${formatCsvNumber(results.finalTax)} ₽`]);
    infoRows.push(['']);
    infoRows.push(['Дата расчета:', new Date().toLocaleDateString('ru-RU')]);
    
    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + infoRows.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `налог_на_имущество_${results.taxYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePropertyTax();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calculator className="w-6 h-6 text-indigo-500" />
          Параметры расчета налога
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Информация о имуществе */}
        <PropertyInfoSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          propertyTypes={propertyTypes}
        />

        {/* Данные о владении */}
        <OwnershipSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Регион и налоговые ставки */}
        <RegionAndRatesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          regionTaxRates={regionTaxRates}
        />

        {/* Налоговые льготы */}
        <PrivilegesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          privilegeTypes={privilegeTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать налог'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;